import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { billingApi } from '../services/billingApi';
import type { BillerDetail as BillerDetailType, PracticeSummary } from '../types/billing';

const BillerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [biller, setBiller] = useState<BillerDetailType | null>(null);
  const [practices, setPractices] = useState<PracticeSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    if (!id) return;
    setLoading(true);
    Promise.all([billingApi.getBiller(id), billingApi.listPractices()])
      .then(([b, ps]) => {
        setBiller(b);
        setPractices(ps);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, [id]);

  const assignedPracticeIds = new Set(biller?.assignedPractices.map((a) => a.practiceId) ?? []);
  const assignedPracticeOptions: PracticeSummary[] =
    practices.filter((p) => assignedPracticeIds.has(p.id));

  const handleAssignmentsChange = async (_: unknown, newValue: PracticeSummary[]) => {
    if (!biller || !id) return;
    const newIds = new Set(newValue.map((p) => p.id));
    const toGrant = [...newIds].filter((pid) => !assignedPracticeIds.has(pid));
    const toRevoke = [...assignedPracticeIds].filter((pid) => !newIds.has(pid));

    setSaving(true);
    setError(null);
    try {
      await Promise.all([
        ...toGrant.map((pid) => billingApi.grantPracticeToBiller(id, pid)),
        ...toRevoke.map((pid) => billingApi.revokePracticeFromBiller(id, pid)),
      ]);
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!biller) return <Alert severity="warning">Biller not found</Alert>;

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>
        {biller.firstName} {biller.lastName}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {biller.email}{biller.archived ? ' (archived)' : ''}
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Assigned practices</Typography>
        <Autocomplete<PracticeSummary, true>
          multiple
          options={practices}
          value={assignedPracticeOptions}
          onChange={handleAssignmentsChange}
          getOptionLabel={(p) => p.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          disabled={saving}
          renderTags={(value, getTagProps) =>
            value.map((p, idx) => {
              const { key, ...tagProps } = getTagProps({ index: idx });
              return <Chip key={key} {...tagProps} label={p.name} />;
            })
          }
          renderInput={(params) => (
            <TextField {...params} label="Practices" placeholder="Add a practice…" />
          )}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Removing a practice immediately revokes this biller's access. History is preserved in the audit log.
        </Typography>
      </Paper>
    </Box>
  );
};

export default BillerDetail;
